import { Badge } from "@d12labs/ui/badge";
import { Button } from "@d12labs/ui/button";
import { List, ListHeader, ListRow } from "@d12labs/ui/data-list";
import { EmptyState } from "@d12labs/ui/empty-state";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { SkeletonRow } from "@d12labs/ui/skeleton";
import { Toolbar } from "@d12labs/ui/toolbar";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ScrollText } from "lucide-react";
import { useState } from "react";
import { speechCoachApi } from "../api";

const SESSION_COLS = [
	{ label: "Date", width: "100px" },
	{ label: "Drill", width: "1.4fr" },
	{ label: "Category", width: "120px" },
	{ label: "Score", width: "80px", align: "right" as const },
	{ label: "Passed", width: "80px", align: "right" as const },
];

// Accepts `string | Date`: the wire value is an ISO string, the derived row
// type says Date — `new Date()` handles both, so no runtime change.
function formatDate(iso: string | Date): string {
	return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function HistoryPage() {
	const navigate = useNavigate();
	const [page, setPage] = useState(1);

	const sessionsQuery = useQuery({
		queryKey: ["speech-coach", "sessions", page],
		queryFn: () => speechCoachApi.listSessions({ query: { page, pageSize: 20 } }),
	});

	const drillsQuery = useQuery({
		queryKey: ["speech-coach", "drills"],
		queryFn: () => speechCoachApi.drills(),
	});

	const allDrills = [...(drillsQuery.data?.curated ?? []), ...(drillsQuery.data?.assigned ?? [])];
	const drillMap = new Map(allDrills.map((d) => [d.id, d]));

	const sessions = sessionsQuery.data;
	const isLoading = sessionsQuery.isLoading;

	return (
		<>
			<PageHeader
				title="History"
				icon={ScrollText}
				subtitle="Your past practice sessions."
				actions={
					<Button variant="ghost" asChild>
						<Link to="/speech-coach">Today</Link>
					</Button>
				}
			/>
			<PageBody className="p-6 space-y-4 mx-auto max-w-3xl w-full">
				{isLoading ? (
					<List>
						<ListHeader columns={SESSION_COLS} />
						{Array.from({ length: 5 }).map((_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders
							<SkeletonRow key={i} columns={SESSION_COLS} />
						))}
					</List>
				) : sessions?.total === 0 ? (
					<EmptyState
						icon={ScrollText}
						title="No sessions yet"
						body="Complete a drill to see your history here."
						primary={
							<Button onClick={() => navigate({ to: "/speech-coach" })}>Start practicing</Button>
						}
					/>
				) : (
					<>
						<List>
							<ListHeader columns={SESSION_COLS} />
							{sessions?.items.map((s) => {
								const drill = drillMap.get(s.drillId);
								const score = s.aiScore != null ? `${Number(s.aiScore).toFixed(1)}/10` : "—";
								const passed =
									s.aiFeedback != null ? (
										<Badge tone={s.aiFeedback.passed ? "success" : "danger"}>
											{s.aiFeedback.passed ? "Passed" : "Failed"}
										</Badge>
									) : (
										<span className="text-ink-subtle">—</span>
									);
								return (
									<ListRow
										key={s.id}
										interactive={false}
										cells={[
											{ value: formatDate(s.createdAt), muted: true },
											{ value: drill?.title ?? s.drillId },
											{
												value: drill ? (
													<Badge tone="primary">{drill.category}</Badge>
												) : (
													<span className="text-ink-subtle">—</span>
												),
											},
											{ value: score, align: "right", muted: !s.aiScore },
											{ value: passed, align: "right" },
										]}
									/>
								);
							})}
						</List>

						{sessions && sessions.pageCount > 1 ? (
							<Toolbar
								left={
									<Button
										variant="outline"
										size="sm"
										onClick={() => setPage((p) => Math.max(1, p - 1))}
										disabled={page <= 1}
									>
										Previous
									</Button>
								}
								right={
									<div className="flex items-center gap-3">
										<span className="text-body-sm text-ink-subtle">
											Page {sessions.page} of {sessions.pageCount}
										</span>
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.min(sessions.pageCount, p + 1))}
											disabled={page >= sessions.pageCount}
										>
											Next
										</Button>
									</div>
								}
							/>
						) : null}
					</>
				)}
			</PageBody>
		</>
	);
}
